import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { showToast } from "../../utils/utils";
import Loader from "../../components/Live/Loader";
import { ReactComponent as PriceIcon } from "../../assets/svg/CourseDetails/Price.svg";
import { ReactComponent as DurationIcon } from "../../assets/svg/CourseDetails/Duration.svg";
import { ReactComponent as StatusIcon } from "../../assets/svg/CourseDetails/Status.svg";
import { ReactComponent as PurchaseIcon } from "../../assets/svg/CourseDetails/Purchase.svg";

const PlacementTestDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = useSelector(selectUserInformations);
    const [test, setTest] = useState(null);
    const [loader, setLoader] = useState(false);
    const enrollment = test?.enrollment ?? test?.pivot ?? null;
    const isEnrolled = Boolean(test?.is_enrolled || enrollment);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoader(true);
            try {
                const { data } = await axios.get(`${API.PlacementTests.get}/${id}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                if (data.status === 200) {
                    setTest(data.data);
                }
            } catch (error) {
                showToast("Error", "Failed to fetch details", "error");
            } finally {
                setLoader(false);
            }
        };
        fetchDetails();
    }, [id, user.token]);

    const handleEnroll = () => {
        // Same enrollment + payment flow as the landing page (correct order for pre-enrollment vs standalone).
        navigate(`/placement-tests/${id}`);
    };

    if (loader) return <div className="flex justify-center py-20"><Loader /></div>;
    if (!test) return <div className="text-center py-20 text-gray-500">Test not found</div>;

    return (
        <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                <div className="p-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">{test.title}</h1>
                    <p className="text-gray-600 mb-8 leading-relaxed text-lg">{test.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                        <div className="flex items-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="p-3 bg-white rounded-lg shadow-sm mr-4">
                                <PriceIcon className="w-8 h-8" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Price</p>
                                <p className="text-xl font-bold text-gray-900">{test.price} USD</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="p-3 bg-white rounded-lg shadow-sm mr-4">
                                <DurationIcon className="w-8 h-8" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Duration</p>
                                <p className="text-xl font-bold text-gray-900">{test.duration_minutes} minutes</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-100 pt-8 gap-4">
                         <div className="flex items-center gap-3">
                            <StatusIcon className="w-6 h-6 text-blue-500" />
                            <span className="text-gray-700 font-medium capitalize">Status: {test.status}</span>
                         </div>
                         
                         {user.student && !isEnrolled && (
                             <button 
                                onClick={handleEnroll}
                                className="flex items-center justify-center gap-3 bg-primary hover:bg-primary-hover text-white px-8 py-3 rounded-xl font-bold text-lg transition-all shadow-md hover:shadow-lg w-full sm:w-auto"
                             >
                                <PurchaseIcon className="w-6 h-6" />
                                ENROLL NOW
                             </button>
                         )}
                         
                         {isEnrolled && (
                             <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                                 <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-bold text-sm text-center w-full">ALREADY ENROLLED</span>
                                 <p className="text-xs text-gray-500">Approval: <span className="font-semibold capitalize">{enrollment?.approval_status || "pending"}</span></p>
                                 
                                 {enrollment?.approval_status === "approved" && !enrollment?.score && (
                                     <a 
                                        href={`${API.examsAppUrl}/exam/${test.exam?.exam_code}?token=${user.token}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white px-6 py-2 rounded-xl font-bold text-base transition-all shadow-md hover:shadow-lg w-full"
                                     >
                                        TAKE TEST
                                     </a>
                                 )}
                                 
                                 {enrollment?.score && (
                                     <div className="mt-2 text-right">
                                         <p className="text-sm font-medium text-gray-500">Your Score</p>
                                         <p className="text-2xl font-black text-primary">{enrollment.score}</p>
                                     </div>
                                 )}
                             </div>
                         )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlacementTestDetailsPage;
